import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { initMercadoPago, Payment, StatusScreen } from '@mercadopago/sdk-react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PaymentFormRef, Transaction } from '@/features/wallet/types';
import { useAuth } from '@auth';
import { useLocale } from '@/shared/localization/useLocale';
import { useMercadoPagoPublicKey } from '@/shared/mercadopago';
import { useMercadoPagoPayment, useTransactionStatusPolling } from '../../hooks';
import PaymentLoadingState from '../PaymentLoadingState';
import PaymentError from '../PaymentError';
import PaymentTimeoutError from '../PaymentTimeoutError';
import Spinner from '@/shared/components/Spinner';

export interface MercadoPagoPaymentFormProps {
  amountCents: number;
  scheduleId?: string;
  onPaymentConfirmed: (transaction: Transaction) => void;
  onReady?: () => void;
  onError?: () => void;
  ref?: Ref<PaymentFormRef>;
}

interface PaymentBrickController {
  getFormData: () => Promise<unknown>;
  unmount: () => void;
}

declare global {
  interface Window {
    paymentBrickController?: PaymentBrickController;
  }
}

export default function MercadoPagoPaymentForm({ ref, amountCents, scheduleId, onPaymentConfirmed, onReady, onError }: MercadoPagoPaymentFormProps) {
  const locale = useLocale();
  const intl = useIntl();
  const { user } = useAuth();
  const publicKey = useMercadoPagoPublicKey();

  const [isSdkReady, setIsSdkReady] = useState(false);
  const [isBrickReady, setIsBrickReady] = useState(false);
  const [pollingError, setPollingError] = useState<ReactNode | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const sdkInitialized = useRef(false);

  // Payment submission hook
  const { submit: submitPayment, isSubmitting, error: paymentError, challengeData, paymentId, transactionId } = useMercadoPagoPayment({
    amountCents,
    scheduleId,
    onSuccess: startPollingCallback,
  });

  const isPixPayment = paymentId != null && challengeData == null;

  const handlePollingFailure = useCallback(() => {
    setPollingError(<PaymentTimeoutError />);
    onError?.();
  }, [onError]);

  // Transaction status polling for payment confirmation
  const { isPolling, startPolling } = useTransactionStatusPolling({
    transactionId,
    onConfirmed: onPaymentConfirmed,
    onTimeout: handlePollingFailure,
    onError: handlePollingFailure,
    ...(isPixPayment && { pollingIntervalMs: 3000, maxAttempts: 200 }),
  });

  function startPollingCallback() {
    startPolling();
  }

  const error = validationError ?? paymentError ?? pollingError;

  // Initialize MercadoPago SDK (one-time initialization)
  useEffect(() => {
    if (!publicKey || sdkInitialized.current) return;

    sdkInitialized.current = true;
    initMercadoPago(publicKey, {
      locale: locale as 'pt-BR' | 'en-US' | 'es-AR',
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-time SDK init
    setIsSdkReady(true);
  }, [publicKey, locale]);

  const initialization = useMemo(() => ({
    amount: amountCents / 100,
    payer: {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      email: user?.email ?? '',
    },
  }), [amountCents, user?.first_name, user?.last_name, user?.email]);

  const customization = useMemo(() => ({
    paymentMethods: {
      creditCard: 'all' as const,
      bankTransfer: 'all' as const,
      maxInstallments: 6,
    },
    visual: {
      hideFormTitle: true,
      hidePaymentButton: true,
      style: { theme: 'default' as const },
    },
  }), []);

  const doSubmit = useCallback(async () => {
    setValidationError(null);

    const controller = window.paymentBrickController;
    if (!controller) throw new Error('Payment form not ready');

    const formData = await controller.getFormData();

    if (formData === null) {
      const errorMessage = intl.formatMessage({ id: 'wallet.payment.validationError' });
      setValidationError(errorMessage);
      throw new Error(errorMessage);
    }

    await submitPayment(formData as Parameters<typeof submitPayment>[0]);
  }, [submitPayment, intl]);

  useImperativeHandle(ref, () => ({ submit: doSubmit }), [doSubmit]);

  const handleBrickReady = useCallback(() => {
    setIsBrickReady(true);
    onReady?.();
  }, [onReady]);

  const handleBrickError = useCallback((err: unknown) => {
    console.error('MercadoPago Brick error:', err);
  }, []);

  const handleStatusScreenReady = useCallback(() => {
    startPolling();
  }, [startPolling]);

  // Required prop but unused since we use getFormData() instead
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleBrickSubmit = useCallback(async () => {}, []);

  // Show loading until SDK is initialized
  if (!isSdkReady) {
    return <PaymentLoadingState />;
  }

  const showOverlay = isSubmitting || (isPolling && !isPixPayment);

  return (
    <div className="relative">
      {error ? (
        <div className="px-4 pt-4">
          <PaymentError message={error} />
        </div>
      ) : null}

      {showOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg">
          <div className="flex items-center">
            <Spinner />
            <span className="ml-2 text-sm text-slate-600">
              <FormattedMessage id={isPolling ? 'wallet.payment.waitingConfirmation' : 'wallet.payment.processing'} />
            </span>
          </div>
        </div>
      )}

      {paymentId ? (
        <div className="px-4 pt-4">
        <StatusScreen
          initialization={{
            paymentId,
            ...(challengeData && {
              additionalInfo: {
                externalResourceURL: challengeData.external_resource_url,
                creq: challengeData.creq,
              },
            }),
          }}
          onReady={handleStatusScreenReady}
          onError={handleBrickError}
        />
        </div>
      ) : (
        <>
          {!isBrickReady && !showOverlay && (
            <div className="flex items-center justify-center py-4">
              <Spinner />
            </div>
          )}

          <div className={isBrickReady ? (showOverlay ? 'pointer-events-none' : '') : 'h-0 overflow-hidden opacity-0'}>
            <Payment
              initialization={initialization}
              customization={customization}
              onSubmit={handleBrickSubmit}
              onReady={handleBrickReady}
              onError={handleBrickError}
            />
          </div>
        </>
      )}
    </div>
  );
}
